import axios from 'axios';
import { invalidate } from '@/utils/cache';
import isMsgs from '@/i18n/is.json';
import enMsgs from '@/i18n/en.json';

// Production (rektra.is) always uses relative paths.
// Preview/dev uses the env variable for cross-origin API calls.
const isProduction = typeof window !== 'undefined' && window.location.hostname === 'rektra.is';
const BACKEND_URL = isProduction ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// ── Error-code → localized message map ─────────────────────────────────────
// Every snake_case `detail` value the backend returns for 4xx errors should
// be listed here so users never see raw English error keys (e.g.
// `subscription_required`) in toasts / banners. Unknown codes fall through.
const ERROR_KEYS = [
  'subscription_required',
  'paywall_required',
  'email_verification_required',
  'email_confirmation_required',
  'plan_limit_reached',
  'access_denied',
  'forbidden',
  'not_found',
  'rate_limited',
  'validation_failed',
  'konto_setup_incomplete',
  'konto_api_unavailable',
  'invoice_failed',
];

function localizeDetailInPlace(error) {
  try {
    const data = error?.response?.data;
    if (!data || typeof data.detail !== 'string') return;
    const raw = data.detail.trim().toLowerCase();
    if (!ERROR_KEYS.includes(raw)) return;
    const lang = localStorage.getItem('rektra_lang') === 'en' ? 'en' : 'is';
    const dict = lang === 'en' ? enMsgs : isMsgs;
    const localized = dict[`errors.${raw}`];
    if (localized) {
      // Preserve the original code on a side-channel so paywall/route logic
      // can still match on the snake_case key (e.g. `paywall_required` →
      // open modal). The visible string for UIs reading `.detail` is now
      // properly localized.
      data.detail_code = raw;
      data.detail = localized;
    }
  } catch { /* never block the error chain on localization failure */ }
}

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
// F1 (fasi 3b): the active company is per TAB. `sessionStorage` is not shared
// between tabs, so switching company in one tab cannot drag another tab along;
// the server validates the header against an active membership and refuses it
// otherwise, so the header can never grant access on its own.
export const getActiveCompanyId = () => sessionStorage.getItem('active_company_id') || '';
export const setActiveCompanyId = (companyId) => {
  if (companyId) sessionStorage.setItem('active_company_id', companyId);
  else sessionStorage.removeItem('active_company_id');
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const companyId = getActiveCompanyId();
  if (companyId) {
    config.headers['X-Company-Id'] = companyId;
  }
  return config;
});

// Handle 401 responses and network errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Network error (offline, DNS failure, CORS, etc.)
    if (!error.response && error.code !== 'ERR_CANCELED') {
      const { toast } = require('sonner');
      if (!navigator.onLine) {
        toast.error('Þú ert ekki tengd/ur netinu', { id: 'offline', duration: 5000 });
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Beiðni rann út á tíma', { id: 'timeout', duration: 4000 });
      } else {
        toast.error('Nettenging rofnaði', { id: 'network', duration: 4000 });
      }
    }

    // Subscription expired/restricted (402)
    if (error.response?.status === 402) {
      const { toast } = require('sonner');
      toast.error('Áskriftin þín er útrunnin. Vinsamlegast endurnýjaðu áskriftina.', { 
        id: 'subscription-expired', 
        duration: 8000 
      });
      // Redirect to billing settings if not already there
      if (!window.location.pathname.includes('/settings') && !window.location.pathname.includes('/billing')) {
        // iter343: /settings?tab=billing renders a BLANK page - the settings
        // tab bar has an Áskrift tab but the real route is /settings/billing.
        window.location.href = '/settings/billing';
      }
      return Promise.reject(error);
    }

    // Paywall required (403 with paywall_required detail) — only for WRITE actions.
    // For GET reads we silently reject so the caller can fall back to empty state;
    // the modal would otherwise cover pages the user legitimately should still see
    // (e.g. Stillingar / Tengingar where they need to enter card details).
    // Match against the raw snake_case code BEFORE we localize the detail string.
    {
      const rawDetail = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail.trim().toLowerCase()
        : null;
      if (error.response?.status === 403 && rawDetail === 'email_confirmation_required') {
        // iter363: the owner never confirmed the email, so the trial ended in
        // a locked state. This is fixable by the user in one tap, so it must
        // never surface as a generic error.
        const { toast } = require('sonner');
        toast.error('Staðfestu netfangið þitt til að halda áfram', {
          id: 'email-confirmation-required',
          duration: 15000,
          className: 'rektra-email-confirm-toast',
          action: {
            label: 'Senda aftur',
            onClick: async () => {
              try {
                await api.post('/auth/send-verification');
                toast.success('Staðfestingarpóstur sendur. Athugaðu pósthólfið.',
                  { id: 'email-confirmation-sent' });
              } catch {
                toast.error('Ekki tókst að senda póstinn. Reyndu aftur eftir smá stund.',
                  { id: 'email-confirmation-failed' });
              }
            },
          },
        });
        localizeDetailInPlace(error);
        return Promise.reject(error);
      }
      if (error.response?.status === 403 && rawDetail === 'paywall_required') {
        const method = (error.config?.method || 'get').toLowerCase();
        if (method !== 'get' && window.__triggerPaywall) window.__triggerPaywall();
        // Localize for any caller that surfaces the message
        localizeDetailInPlace(error);
        return Promise.reject(error);
      }
    }

    if (error.response?.status === 401) {
      // Don't clear auth if the request was the login endpoint itself
      const url = error.config?.url || '';
      if (url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/oauth')) {
        return Promise.reject(error);
      }
      // If impersonating, restore superadmin session instead of wiping everything
      const superadminToken = localStorage.getItem('superadmin_token');
      if (superadminToken) {
        localStorage.setItem('auth_token', superadminToken);
        localStorage.removeItem('superadmin_token');
        localStorage.removeItem('impersonating');
        window.location.href = '/platform';
        return Promise.reject(error);
      }
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      // Don't redirect if on auth pages
      if (!window.location.pathname.includes('/login') && 
          !window.location.pathname.includes('/register') &&
          !window.location.hash.includes('session_id')) {
        window.location.href = '/login';
      }
    }
    // A 5xx carries FastAPI's English "Internal Server Error" (or a raw
    // validation dump) in `detail`. Users must never see that, so we replace
    // it with an Icelandic sentence. The original is kept on `detail_raw` for
    // debugging, and `detail_code` so callers can still branch on it.
    if (error.response?.status >= 500) {
      try {
        const data = error.response.data || {};
        const lang = localStorage.getItem('rektra_lang') === 'en' ? 'en' : 'is';
        data.detail_raw = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail || '');
        data.detail_code = 'server_error';
        data.detail = lang === 'en'
          ? 'Something went wrong on our side. Please try again.'
          : 'Eitthvað fór úrskeiðis hjá okkur. Reyndu aftur.';
        error.response.data = data;
      } catch { /* never block the error chain */ }
    }

    // Localize any remaining known error codes so callers reading
    // `err.response?.data?.detail` automatically get the Icelandic/English
    // sentence rather than the raw `snake_case` key.
    localizeDetailInPlace(error);
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  oauthSession: (sessionId) => api.post(`/auth/oauth/session?session_id=${sessionId}`),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  changePassword: (data) => api.post('/auth/change-password', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  getInviteInfo: (token) => api.get(`/auth/invite/${token}`),
  acceptInvite: (data) => api.post('/auth/accept-invite', data),
  switchCompany: (companyId) => api.post('/auth/switch-company', { company_id: companyId }),
  sendVerification: () => api.post('/auth/send-verification'),
  verifyEmail: (data) => api.post('/auth/verify-email', data),
};

// Dashboard API
export const dashboardAPI = {
  get: (month) => api.get(`/dashboard${month ? `?month=${month}` : ''}`),
  myTasks: (daysAhead = 7) => api.get(`/dashboard/my-tasks?days_ahead=${daysAhead}`),
};

// Projects API
export const projectsAPI = {
  list: (activeOnly = true) => api.get(`/projects?active_only=${activeOnly}`),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data).then(r => { invalidate('projects', 'dashboard'); return r; }),
  update: (id, data) => api.patch(`/projects/${id}`, data).then(r => { invalidate('projects', 'dashboard'); return r; }),
  delete: (id) => api.delete(`/projects/${id}`).then(r => { invalidate('projects', 'dashboard'); return r; }),
  getTimeEntries: (id) => api.get(`/projects/${id}/time-entries`),
  // Phase 2: every number on the project top card, computed server-side.
  getTopCard: (id) => api.get(`/projects/${id}/top-card`),
  getReceipts: (id) => api.get(`/projects/${id}/receipts`),
  // iter316: Flexible project report (date range + verk filter)
  getReport: (id, params) => api.get(`/projects/${id}/report`, { params }),
  reportPdf: (id, params) => api.get(`/projects/${id}/report/pdf`, {
    params, responseType: 'blob', timeout: 60000,
  }),
  // iter314: Weekly project report (Innifalið vs Aukaverk)
  getWeeklyReport: (id, week) => api.get(`/projects/${id}/weekly-report`, { params: { week } }),
  weeklyReportPdf: (id, week) => api.get(`/projects/${id}/weekly-report/pdf`, {
    params: { week }, responseType: 'blob', timeout: 60000,
  }),
  updateStatus: (id, status) => api.patch(`/projects/${id}/status`, null, { params: { status } }).then(r => { invalidate('projects', 'dashboard'); return r; }),
  updateInvoiceStatus: (id, status) => api.patch(`/projects/${id}/invoice-status`, null, { params: { invoice_status: status } }).then(r => { invalidate('projects'); return r; }),
  markBilled: (id) => api.post(`/projects/${id}/mark-billed`).then(r => { invalidate('projects', 'dashboard'); return r; }),
  // Diary
  getDiary: (id) => api.get(`/projects/${id}/diary`),
  createDiary: (id, data) => api.post(`/projects/${id}/diary`, data),
  updateDiary: (id, entryId, data) => api.patch(`/projects/${id}/diary/${entryId}`, data),
  deleteDiary: (id, entryId) => api.delete(`/projects/${id}/diary/${entryId}`),
  // Reminders
  getReminders: (id) => api.get(`/projects/${id}/reminders`),
  createReminder: (id, data) => api.post(`/projects/${id}/reminders`, data),
  updateReminder: (id, remId, data) => api.patch(`/projects/${id}/reminders/${remId}`, data),
  deleteReminder: (id, remId) => api.delete(`/projects/${id}/reminders/${remId}`),
  // Jobs
  getJobs: (id) => api.get(`/projects/${id}/jobs`),
  createJob: (id, data) => api.post(`/projects/${id}/jobs`, data),
  updateJob: (id, jobId, data) => api.patch(`/projects/${id}/jobs/${jobId}`, data),
  deleteJob: (id, jobId) => api.delete(`/projects/${id}/jobs/${jobId}`),
  // iter321: Verk detail — everything linked to a single verk
  getJobDetail: (id, jobId) => api.get(`/projects/${id}/jobs/${jobId}/detail`),
  jobDetailPdf: (id, jobId) => api.get(`/projects/${id}/jobs/${jobId}/detail/pdf`, {
    responseType: 'blob', timeout: 60000,
  }),
  // Client names
  getClientNames: () => api.get('/projects/client-names'),
  // Activity Images
  uploadActivityImage: (projectId, formData) => api.post(`/projects/${projectId}/activity-images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteActivityImage: (projectId, data) => api.delete(`/projects/${projectId}/activity-images`, { data }),
  getActivityImageUrl: (imageKey) => api.get(`/projects/activity-images/url`, { params: { image_key: imageKey } }),
  // Project Documents
  getDocuments: (id) => api.get(`/projects/${id}/documents`),
  uploadDocument: (id, formData) => api.post(`/projects/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteDocument: (id, docId) => api.delete(`/projects/${id}/documents/${docId}`),
  getDocumentUrl: (id, docId) => api.get(`/projects/${id}/documents/${docId}/url`),
  getAllImages: (id) => api.get(`/projects/${id}/all-images`),
  // Materials
  getMaterials: (id) => api.get(`/projects/${id}/materials`),
  createMaterial: (id, data) => api.post(`/projects/${id}/materials`, data),
  updateMaterial: (id, matId, data) => api.patch(`/projects/${id}/materials/${matId}`, data),
  deleteMaterial: (id, matId) => api.delete(`/projects/${id}/materials/${matId}`),
  // Iter306: Unforeseen expenses (Ófyrirséð útgjöld) — absorbed costs that
  // affect actual project margin but NEVER appear on invoices.
  getUnforeseenExpenses: (id) => api.get(`/projects/${id}/unforeseen-expenses`),
  createUnforeseenExpense: (id, data) => api.post(`/projects/${id}/unforeseen-expenses`, data),
  updateUnforeseenExpense: (id, eid, data) => api.patch(`/projects/${id}/unforeseen-expenses/${eid}`, data),
  deleteUnforeseenExpense: (id, eid) => api.delete(`/projects/${id}/unforeseen-expenses/${eid}`),
  // Billing
  getBilling: (id) => api.get(`/projects/${id}/billing`),
  getAukavinna: (id) => api.get(`/projects/${id}/aukavinna`),
  aukavinnaPdf: (id, entryIds) => api.post(`/projects/${id}/aukavinna/pdf`,
    { entry_ids: entryIds }, { responseType: 'blob' }),
  getBillingRangePreview: (id, fromDate, toDate) => api.get(`/projects/${id}/billing/range-preview?from_date=${fromDate}&to_date=${toDate}`),
  // Server-computed preview of a fixed-price invoice's totals — the
  // modal must NOT compute `* 1.24` client-side. Same math the backend
  // persists on create.
  previewFixedPriceInvoice: (id, body) => api.post(`/projects/${id}/billing/preview-fixed-price-invoice`, body),
  createBillingPeriod: (id, data) => api.post(`/projects/${id}/billing/periods`, data).then(r => { invalidate('projects'); return r; }),
  deleteBillingPeriod: (id, periodId) => api.delete(`/projects/${id}/billing/periods/${periodId}`).then(r => { invalidate('projects'); return r; }),
  billingPeriodPdf: (id, periodId) => api.get(`/projects/${id}/billing/periods/${periodId}/pdf`, { responseType: 'blob' }),
};

export const costLibraryAPI = {
  list: () => api.get('/cost-library'),
  create: (data) => api.post('/cost-library', data),
  update: (itemId, data) => api.patch(`/cost-library/${itemId}`, data),
  remove: (itemId) => api.delete(`/cost-library/${itemId}`),
};

export const unitPricesAPI = {
  list: () => api.get('/unit-prices'),
  create: (data) => api.post('/unit-prices', data),
  update: (itemId, data) => api.patch(`/unit-prices/${itemId}`, data),
  remove: (itemId) => api.delete(`/unit-prices/${itemId}`),
};

// Named bundles of unit_prices — user clicks "Sækja template" and the
// selected lines are pasted into the current quote as line items.
// See backend `unit_price_template_router`.
export const unitPriceTemplatesAPI = {
  list: () => api.get('/unit-price-templates'),
  create: (data) => api.post('/unit-price-templates', data),
  update: (id, data) => api.patch(`/unit-price-templates/${id}`, data),
  remove: (id) => api.delete(`/unit-price-templates/${id}`),
  expand: (id) => api.get(`/unit-price-templates/${id}/expand`),
};

export const quotesAPI = {
  list: () => api.get('/quotes'),
  get: (id) => api.get(`/quotes/${id}`),
  create: (data) => api.post('/quotes', data),
  update: (id, data) => api.patch(`/quotes/${id}`, data),
  remove: (id) => api.delete(`/quotes/${id}`),
  duplicate: (id) => api.post(`/quotes/${id}/duplicate`),
  newVersion: (id) => api.post(`/quotes/${id}/new-version`),
  generatePdf: (id) => api.post(`/quotes/${id}/generate-pdf`, {}, { responseType: 'blob' }),
  getPdf: (id) => api.get(`/quotes/${id}/pdf`, { responseType: 'blob' }),
  send: (id) => api.post(`/quotes/${id}/send`),
  createProject: (id) => api.post(`/quotes/${id}/create-project`),
  aiAssist: (id) => api.post(`/quotes/${id}/ai-assist`),
  aiDescription: (id) => api.post(`/quotes/${id}/ai-description`),
  aiReview: (id) => api.post(`/quotes/${id}/ai-review`),
  getDefaultTerms: () => api.get('/quotes/default-terms'),
  // Server-computed live preview of totals — the frontend must NOT
  // recompute its own copy. `Math.round()` (JS) and `round()` (Python)
  // disagree on exact .5 ties in a fraction of ordinary inputs, so
  // any client-side implementation drifts from the saved backend total.
  previewTotals: (lines) => api.post('/quotes/preview-totals', { lines }),
  // Attachments (viðhengi) — max 5 per quote. `file` is a native File/Blob
  // object; axios wraps it in FormData automatically when browsers detect the
  // upload key. See backend `quotes_router` upload endpoint.
  uploadAttachment: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/quotes/${id}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAttachment: (id, attachmentId) =>
    api.delete(`/quotes/${id}/attachments/${attachmentId}`),
  getAttachmentDownload: (id, attachmentId) =>
    api.get(`/quotes/${id}/attachments/${attachmentId}/download`),
};

export const payrollAPI = {
  listBonuses: (params) => api.get('/payroll/bonuses', { params }),
  createBonus: (data) => api.post('/payroll/bonuses', data),
  deleteBonus: (id) => api.delete(`/payroll/bonuses/${id}`),
  overview: (start_date, end_date) => api.get('/payroll/overview', { params: { start_date, end_date } }),
};

// Tenant invoicing API — contractors invoicing THEIR customers via Konto.
// Material takeoff (Efnismagntaka / Tilboðsgerð) — AI-powered project BOM.
export const materialTakeoffAPI = {
  analyze: (formData) => api.post('/material-takeoff/analyze', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }),
  list: () => api.get('/material-takeoff'),
  get: (id) => api.get(`/material-takeoff/${id}`),
  remove: (id) => api.delete(`/material-takeoff/${id}`),
  pdfUrl: (id) => `${api.defaults.baseURL}/material-takeoff/${id}/pdf`,
  downloadPdf: (id) => api.get(`/material-takeoff/${id}/pdf`, { responseType: 'blob', timeout: 60000 }),
  // Manual mode + edit support
  catalog: (q = '', limit = 25) => api.get('/material-takeoff/_catalog/standard', { params: { q, limit } }),
  createManual: (data) => api.post('/material-takeoff/manual', data),
  update: (id, patch) => api.patch(`/material-takeoff/${id}`, patch),
};

export const tenantInvoicesAPI = {
  list: (params) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  remove: (id) => api.delete(`/invoices/${id}`),
  send: (id, payload = {}) => api.post(`/invoices/${id}/send`, payload),
  void: (id, payload = {}) => api.post(`/invoices/${id}/void`, payload),
  paydayPaymentTypes: () => api.get('/invoices/payday/payment-types'),
  // Server-computed live-preview of totals for the InvoiceEditorModal.
  // The frontend MUST NOT recompute money math client-side — JS's
  // `Math.round` diverges from Python's banker's rounding on halves,
  // and the modal would show a subtotal that differed from the saved
  // invoice by 1 kr on a fraction of realistic inputs.
  previewTotals: (payload) => api.post('/invoices/preview-totals', payload),
  pdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  verkyfirlitPdf: (id) => api.get(`/invoices/${id}/verkyfirlit-pdf`, { responseType: 'blob' }),
  summaryCsv: (id) => api.get(`/invoices/${id}/summary.csv`, { responseType: 'text' }),
  // Project-scoped helpers
  draftFromProject: (projectId, payload = {}) => api.post(`/projects/${projectId}/invoice/draft`, payload),
  listForProject: (projectId) => api.get(`/projects/${projectId}/invoices`),
  billableSources: (projectId, invoiceId) => api.get(
    `/projects/${projectId}/billable-sources`,
    { params: invoiceId ? { invoice_id: invoiceId } : {} },
  ),
};

// My Jobs API (cross-project)
export const jobsAPI = {
  my: () => api.get('/jobs/my'),
  linkable: () => api.get('/jobs/linkable'),
};

// Categories API
export const categoriesAPI = {
  list: () => api.get('/categories'),
  overview: (month) => api.get(`/categories/overview${month ? `?month=${month}` : ''}`),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
  seedDefaults: () => api.post('/categories/seed-defaults'),
};

// Receipts API
export const receiptsAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/receipts?${query}`);
  },
  get: (id) => api.get(`/receipts/${id}`),
  create: (formData) => api.post('/receipts', formData, {
    headers: { 'Content-Type': undefined },
  }).then(r => { invalidate('receipts', 'dashboard'); return r; }),
  quickCreate: (formData) => api.post('/receipts/quick', formData, {
    headers: { 'Content-Type': undefined },
  }).then(r => { invalidate('receipts', 'dashboard'); return r; }),
  update: (id, data) => api.patch(`/receipts/${id}`, data).then(r => { invalidate('receipts', 'dashboard'); return r; }),
  delete: (id) => api.delete(`/receipts/${id}`).then(r => { invalidate('receipts', 'dashboard'); return r; }),
  confirmVat: (id) => api.post(`/receipts/${id}/confirm-vat`).then(r => { invalidate('receipts', 'dashboard'); return r; }),
  review: (id) => api.post(`/receipts/${id}/review`).then(r => { invalidate('receipts', 'dashboard'); return r; }),
  unreview: (id) => api.post(`/receipts/${id}/unreview`).then(r => { invalidate('receipts', 'dashboard'); return r; }),
  batchReview: (receipt_ids) => api.post('/receipts/batch-review', { receipt_ids }).then(r => { invalidate('receipts', 'dashboard'); return r; }),
  batchDownloadUrls: (receipt_ids) => api.post('/receipts/batch-download-urls', { receipt_ids }),
  updateLineItems: (id, data) => api.patch(`/receipts/${id}/line-items`, data).then(r => { invalidate('receipts'); return r; }),
  analyze: (formData) => api.post('/receipts/analyze', formData, {
    headers: { 'Content-Type': undefined },
    // iter324: PDF/receipt uploads can be 5-15MB from desktop over slow
    // mobile-tethered wifi. Backend returns 202 quickly once the upload
    // finishes; the timeout only covers the upload phase. 15s was
    // aborting large-PDF uploads mid-flight, surfacing a red "OCR failed"
    // toast even though the backend was fine. 90s matches the polling
    // window and covers real-world upload durations.
    timeout: 90000,
  }),
  analyzeStatus: (taskId) => api.get(`/receipts/analyze/status/${taskId}`),
  checkDuplicate: (data) => api.post('/receipts/check-duplicate', data),
  setProjectCost: (receiptId, projectCost, projectBillable) => api.patch(`/receipts/${receiptId}/project-cost`, { project_cost: projectCost, project_billable: projectBillable }),
  resetProjectCost: (receiptId) => api.patch(`/receipts/${receiptId}/project-cost`, { project_cost: null, project_billable: null }),
};

// Time Entries API
export const timeEntriesAPI = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/time-entries?${query}`);
  },
  billedRanges: () => api.get('/time-entries/billed-ranges'),
  create: (data) => api.post('/time-entries', data).then(r => { invalidate('time-entries', 'dashboard', 'hours'); return r; }),
  update: (id, data) => api.patch(`/time-entries/${id}`, data).then(r => { invalidate('time-entries', 'dashboard', 'hours'); return r; }),
  delete: (id) => api.delete(`/time-entries/${id}`).then(r => { invalidate('time-entries', 'dashboard', 'hours'); return r; }),
};

// Monthly Locks API
export const locksAPI = {
  list: () => api.get('/locks'),
  getChecklist: (month, lang) => api.get(`/locks/checklist/${month}${lang ? `?lang=${lang}` : ''}`),
  lock: (month) => api.post('/locks', { month }),
  unlock: (month) => api.delete(`/locks/${month}`),
};

// Exports API
export const exportsAPI = {
  download: (month, format = 'rektra') => api.get(`/exports/${month}?format=${format}`, { responseType: 'blob' }),
  getFormats: () => api.get('/exports/formats'),
};

// Users API
export const usersAPI = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  invite: (data) => api.post('/users/invite', data),
  pendingInvites: () => api.get('/users/pending-invites'),
  cancelInvite: (email) => api.post('/users/pending-invites/cancel', { email }),
  updateProfile: (data) => api.patch('/auth/me', data),
};

// Onboarding API
export const onboardingAPI = {
  getStatus: () => api.get('/onboarding/status'),
  seedDemo: () => api.post('/onboarding/seed-demo'),
  clearData: () => api.delete('/onboarding/clear-data'),
  logEvent: (data) => api.post('/onboarding/event', data).catch(() => {}),
};

// Company Settings API
export const companyAPI = {
  getSettings: () => api.get('/company/settings'),
  updateSettings: (data) => api.patch('/company/settings', data),
  getPayPeriod: (offset = 0) => api.get('/company/pay-period', { params: { offset } }),
  uploadLogo: (formData) => api.post('/company/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteLogo: () => api.delete('/company/logo'),
  getLogoUrl: () => api.get('/company/logo-url'),
  deleteSelf: (companyName) => api.delete('/company', { data: { company_name: companyName } }),
  getBillingStatus: () => api.get('/company/billing-status'),
  getUnbilledSummary: (config) => api.get('/company/unbilled-summary', config),
  getPaddleConfig: () => api.get('/company/paddle-config'),
  cancelSubscription: () => api.post('/company/cancel-subscription'),
  createSubscription: (data) => api.post('/company/create-subscription', data),
  createCheckoutSession: (plan) => api.post(`/company/create-checkout-session?plan=${plan}`),
  getSubscription: () => api.get('/company/subscription'),
  getChartOfAccounts: () => api.get('/company/chart-of-accounts'),
  updateChartOfAccounts: (data) => api.patch('/company/chart-of-accounts', data),
  prefillFromKennitala: (kennitala) => api.get('/company/prefill-from-kennitala', { params: { kennitala } }),
  // Multi-company: spin up another company under the same login. Returns
  // the new company + a fresh JWT scoped to it (caller can immediately
  // switch into it without a second roundtrip).
  createAdditional: (data) => api.post('/company/create-additional', data),
};

// Konto-based Subscription Billing API
export const billingKontoAPI = {
  plansCatalog: () => api.get('/billing/plans'),
  getSubscription: () => api.get('/billing/subscription'),
  selectPlan: (plan) => api.post('/billing/select-plan', { plan }),
  changePlan: (plan) => api.post('/billing/change-plan', { plan }),
  planLimit: () => api.get('/billing/plan-limit'),
  upgradeAndAddUser: (payload) => api.post('/billing/upgrade-and-add-user', payload),
  cancel: () => api.post('/billing/cancel'),
  retryActivation: () => api.post('/billing/retry-activation'),
  redeemCode: (code) => api.post('/billing/redeem-code', { code }),
  sendInvoiceNow: () => api.post('/billing/send-invoice-now'),
  updateInvoiceEmail: (email) => api.post('/billing/invoice-email', { email }),
  invoices: () => api.get('/billing/invoices'),
  sendPaywallCode: (opts = {}) => api.post('/billing/paywall/send-code', opts),
  activatePaywall: (data) => api.post('/billing/paywall/activate', data),
};

// Reports API
export const reportsAPI = {
  vatSummary: (params) => api.get('/reports/vat-summary', { params }),
  bookkeeperSummary: (month) => api.get('/reports/bookkeeper-summary', { params: { month } }),
  financialSummary: (params) => api.get('/reports/financial-summary', { params }),
  financialSummaryPdf: (params) => api.get('/reports/financial-summary/pdf', {
    params,
    responseType: 'blob',
  }),
  financialOverview: (params) => api.get('/reports/financial-overview', { params }),
  timeReportPdf: (params) => api.get('/time-entries/report/pdf', {
    params,
    responseType: 'blob',
  }),
  timeReportPdfPerEmployee: (params) => api.get('/time-entries/report/pdf-per-employee', {
    params,
    responseType: 'blob',
  }),
};

// Platform (Superadmin) API
export const integrationsAPI = {
  paydayStatus: () => api.get('/integrations/payday/status'),
  paydaySaveCredentials: (data) => api.post('/integrations/payday/save-credentials', data),
  paydayTest: () => api.post('/integrations/payday/test'),
  paydayDisconnect: () => api.post('/integrations/payday/disconnect'),
  paydayGetSettings: () => api.get('/integrations/payday/settings'),
  paydayUpdateSettings: (data) => api.put('/integrations/payday/settings', data),
  paydayPushBillingPeriod: (periodId) => api.post(`/integrations/payday/billing-periods/${periodId}/push`),
  paydayRefreshBillingPeriod: (periodId) => api.post(`/integrations/payday/billing-periods/${periodId}/refresh-status`),
  paydayPushReceipt: (receiptId) => api.post(`/integrations/payday/receipts/${receiptId}/push`),
  nationalRegistryBusiness: (kennitala) => api.get(`/integrations/national-registry/business/${kennitala}`),

  // DK (dk+)
  dkStatus: () => api.get('/integrations/dk/status'),
  dkSaveToken: (data) => api.post('/integrations/dk/save-token', data),
  dkTest: () => api.post('/integrations/dk/test'),
  dkDisconnect: () => api.post('/integrations/dk/disconnect'),
  dkGetSettings: () => api.get('/integrations/dk/settings'),
  dkUpdateSettings: (data) => api.put('/integrations/dk/settings', data),
  dkPushBillingPeriod: (periodId) => api.post(`/integrations/dk/billing-periods/${periodId}/push`),

  // Konto.is
  kontoStatus: () => api.get('/integrations/konto/status'),
  kontoSaveCredentials: (data) => api.post('/integrations/konto/save-credentials', data),
  kontoTest: () => api.post('/integrations/konto/test'),
  kontoDisconnect: () => api.post('/integrations/konto/disconnect'),
  kontoGetSettings: () => api.get('/integrations/konto/settings'),
  kontoUpdateSettings: (data) => api.put('/integrations/konto/settings', data),
  kontoPushBillingPeriod: (periodId) => api.post(`/integrations/konto/billing-periods/${periodId}/push`),
  kontoPushReceipt: (receiptId) => api.post(`/integrations/konto/receipts/${receiptId}/push`),

  // Primary accounting system routing
  getPrimaryAccounting: () => api.get('/integrations/primary'),
  setPrimaryAccounting: (data) => api.put('/integrations/primary', data),

  // Konto-compatible read API (Rektra-issued API key for external integrations)
  kontoCompatStatus: () => api.get('/integrations/konto-compat/status'),
  kontoCompatIssueKey: (data) => api.post('/integrations/konto-compat/issue-key', data || {}),
  kontoCompatRevokeKey: () => api.post('/integrations/konto-compat/revoke-key'),
};

export const customersAPI = {
  list: (params = {}) => api.get('/customers', { params }),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.patch(`/customers/${id}`, data),
  remove: (id) => api.delete(`/customers/${id}`),
  fromKennitala: (kennitala) => api.post('/customers/from-kennitala', { kennitala }),
};

export const platformAPI = {
  overview: () => api.get('/platform/overview'),
  companies: (params = {}) => api.get('/platform/companies', { params }),
  companyDetail: (id) => api.get(`/platform/companies/${id}`),
  suspend: (id) => api.post(`/platform/companies/${id}/suspend`),
  unsuspend: (id) => api.post(`/platform/companies/${id}/unsuspend`),
  impersonate: (userId) => api.post('/platform/impersonate', { user_id: userId }),
  waitlist: (params = {}) => api.get('/platform/waitlist', { params }),
  waitlistExport: () => api.get('/platform/waitlist/export', { responseType: 'blob' }),
  waitlistMarkInvited: (email) => api.patch(`/platform/waitlist/${encodeURIComponent(email)}/invite`),
  waitlistSendLaunch: () => api.post('/platform/waitlist/send-launch-email'),
};

export default api;
